import { useEffect, useRef } from "react";

// Auto-drifting horizontal rail that users can also swipe / drag / wheel. Content must be duplicated (A+A) for seamless looping.
export function useAutoScroll(speed = 0.5, ready = true) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !ready) return;
    let pos = el.scrollLeft;
    let paused = false;
    let raf;
    let resumeT;
    let drag = null;

    const half = () => el.scrollWidth / 2;
    const wrap = () => {
      const h = half();
      if (h <= 0) return;
      if (el.scrollLeft >= h) el.scrollLeft -= h;
      else if (el.scrollLeft <= 0) el.scrollLeft += h;
    };
    const pause = () => {
      paused = true;
      clearTimeout(resumeT);
    };
    const resumeLater = (ms = 1800) => {
      clearTimeout(resumeT);
      resumeT = setTimeout(() => {
        pos = el.scrollLeft;
        paused = false;
      }, ms);
    };
    const tick = () => {
      if (!paused && !document.hidden && el.scrollWidth > el.clientWidth) {
        pos += speed;
        if (pos >= half()) pos -= half();
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(tick);
    };

    const onEnter = () => pause();
    const onLeave = () => {
      drag = null;
      resumeLater(600);
    };
    const onTouchStart = () => pause();
    const onTouchEnd = () => resumeLater();
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY) && !e.shiftKey) return;
      pause();
      resumeLater();
    };
    const onScroll = () => {
      if (paused) wrap();
    };
    const onDown = (e) => {
      if (e.pointerType !== "mouse") return;
      drag = { x: e.clientX, left: el.scrollLeft, moved: false };
    };
    const onMove = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > 4) drag.moved = true;
      el.scrollLeft = drag.left - dx;
    };
    const onUp = () => {
      if (drag?.moved) {
        const block = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        el.addEventListener("click", block, { capture: true, once: true });
        setTimeout(() => el.removeEventListener("click", block, { capture: true }), 50);
      }
      drag = null;
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    const onDragStart = (e) => e.preventDefault();
    el.addEventListener("dragstart", onDragStart);
    raf = requestAnimationFrame(tick);

    return () => {
      el.removeEventListener("dragstart", onDragStart);
      cancelAnimationFrame(raf);
      clearTimeout(resumeT);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };
  }, [speed, ready]);

  return ref;
}
